import { Alert } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  PatientShouldNotBeMatchedReason,
  ProviderNotMatchReason,
} from '../../../generated/graphql';
import { selectMatching } from '../../../redux/matching';

const ALERT_TEXT_PREFIX = 'Unfortunately, the clinician you chose on Zocdoc';
const REASONS_WITH_BANNERS = [
  ProviderNotMatchReason.NoMedicalLicenseInPatientState,
  ProviderNotMatchReason.DoesNotTreatAgeGroup,
  ProviderNotMatchReason.SpecialtyNotTreatedDealbreaker,
];

const formatListOfReasons = (reasons: string[]): string => {
  if (reasons.length === 0) {
    throw new Error('Must provide at least one reason');
  }
  if (reasons.length === 1) {
    return reasons[0];
  }
  if (reasons.length === 2) {
    return reasons.join(' and ');
  }
  const firstItems = reasons.slice(0, -1);
  const lastItem = reasons[reasons.length - 1];
  const firstPart = firstItems.join(', ');
  return `${firstPart}, and ${lastItem}`;
};

const providerNotMatchReasonToCopy = (
  reason: ProviderNotMatchReason,
  zdProviderTreatmentDealbreakerReasons?: string[]
): string => {
  if (reason === ProviderNotMatchReason.NoMedicalLicenseInPatientState)
    return 'isn’t licensed in your state';
  if (reason === ProviderNotMatchReason.DoesNotTreatAgeGroup)
    return 'can’t treat patients in your age range';
  if (reason === ProviderNotMatchReason.SpecialtyNotTreatedDealbreaker)
    return `doesn’t treat ${formatListOfReasons(
      zdProviderTreatmentDealbreakerReasons
    )}`;

  // generic fallback alert text
  return 'Based on your responses, the clinician you chose on Zocdoc won’t be able to provide the care you’re looking for. Fortunately, we have more doctors who might be a better fit.';
};

const getTextFromPatientShouldNotBeMatchedReasons = (
  patientShouldNotBeMatchedReasons: PatientShouldNotBeMatchedReason[]
): string => {
  if (patientShouldNotBeMatchedReasons.length === 0)
    throw new Error('Must provide at least one reason');

  if (patientShouldNotBeMatchedReasons.length > 1)
    return 'Unfortunately, the clinician you chose on Zocdoc may not be the best fit for you.';
  if (patientShouldNotBeMatchedReasons.length > 0) {
    const patientShouldNotBeMatchedReason = patientShouldNotBeMatchedReasons[0];
    if (
      patientShouldNotBeMatchedReason ===
      PatientShouldNotBeMatchedReason.HasMedicare
    )
      return 'We need to verify that the clinician you selected on Zocdoc is licensed to accept Medicare in your state. The best way to do this and set up a first visit is with a quick phone call.';
    if (
      patientShouldNotBeMatchedReason ===
      PatientShouldNotBeMatchedReason.HasOonPlan
    )
      return 'Before we match you with a clinician, we need to double check your insurance coverage.';
    if (
      patientShouldNotBeMatchedReason === PatientShouldNotBeMatchedReason.Minor
    )
      return 'The clinician you selected on Zocdoc may not be able to provide the care you’re looking for. We’ll help you find a Talkiatry psychiatrist who can.';
    if (
      patientShouldNotBeMatchedReason ===
      PatientShouldNotBeMatchedReason.NotOpenToTakingMedication
    )
      return 'Our clinicians specialize in providing medication management alongside light talk therapy. If you’d like to speak with a Care Coordinator about what this means, or to schedule time with a doctor to discuss your options, let’s set up a call.';
    if (
      patientShouldNotBeMatchedReason ===
      PatientShouldNotBeMatchedReason.NoReasonsSelected
    )
      return 'Based on your responses, the clinician you chose on Zocdoc won’t be able to provide the care you’re looking for. Fortunately, we have more doctors who might be a better fit.';
  }
};

const formatReasons = (
  reasons: ProviderNotMatchReason[],
  zdProviderTreatmentDealbreakerReasons?: string[]
) => {
  const formattedReasons = reasons.map(reason =>
    providerNotMatchReasonToCopy(reason, zdProviderTreatmentDealbreakerReasons)
  );

  return `${ALERT_TEXT_PREFIX} ${formatListOfReasons(formattedReasons)}.`;
};

/**
 * @param patientShouldNotBeMatchedReasons a non-null non-empty array of reasons the patient shouldn't be matched
 * @param providerNotMatchReasons a non-null non-empty array of reasons the provider didn't match with this patient
 * @param zdProviderTreatmentDealbreakerReasons an optional array of dealbreaker reasons for the zocdoc provider.
 *  Must be provided if ProviderNotMatchReason.SpecialtyNotTreatedDealbreaker is in providerNotMatchReasons.
 * @returns the text to display in the alert component.
 */
const getAlertTextFromReasons = (
  patientShouldNotBeMatchedReasons: PatientShouldNotBeMatchedReason[],
  providerNotMatchReasons: ProviderNotMatchReason[],
  zdProviderTreatmentDealbreakerReasons?: string[]
): string => {
  if (
    !patientShouldNotBeMatchedReasons?.length &&
    !providerNotMatchReasons?.length
  )
    throw new Error('No reasons provided');
  if (
    providerNotMatchReasons?.includes(
      ProviderNotMatchReason.SpecialtyNotTreatedDealbreaker
    ) &&
    !zdProviderTreatmentDealbreakerReasons?.length
  )
    throw new Error(
      `Must provide the provider's dealbreaker reason for visit if ${ProviderNotMatchReason.SpecialtyNotTreatedDealbreaker} is a reason the match was rejected.`
    );

  if (patientShouldNotBeMatchedReasons?.length)
    return getTextFromPatientShouldNotBeMatchedReasons(
      patientShouldNotBeMatchedReasons
    );

  const providerReasonsWithBanners = providerNotMatchReasons.filter(reason =>
    REASONS_WITH_BANNERS.includes(reason)
  );
  if (providerReasonsWithBanners.length === 0) {
    // Don't have any reasons with special banners, so return the generic one.
    return providerNotMatchReasonToCopy(providerNotMatchReasons[0]);
  }
  return formatReasons(
    providerReasonsWithBanners,
    zdProviderTreatmentDealbreakerReasons
  );
};

const ZocdocNotAMatchAlert = () => {
  const {
    patientShouldNotBeMatchedReasons,
    zdProviderMatchFailureReasons,
    zdProviderTreatmentDealbreakerReasons,
  } = useSelector(selectMatching);

  if (
    !patientShouldNotBeMatchedReasons?.length &&
    !zdProviderMatchFailureReasons?.length
  )
    return null;
  return (
    <Alert severity="info">
      {getAlertTextFromReasons(
        patientShouldNotBeMatchedReasons,
        zdProviderMatchFailureReasons,
        zdProviderTreatmentDealbreakerReasons
      )}
    </Alert>
  );
};
export default ZocdocNotAMatchAlert;
